import React, { ChangeEvent, useEffect, useRef } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import * as Button from '../../../components/button/button';
import { Actions, Dialog, Title, Content } from '../../../components/dialog/dialog';
import { FileInputLarge } from '../../../components/forms/file';
import { Label, TextInputLarge } from '../../../components/forms/input';
import { Select2 } from '../../../components/forms/select';
import { Icons } from '../../../components/icons/icons';
import { useDeleteMutation } from '../../../hooks/use-mutations';
import { Pageable, usePagination } from '../../../hooks/use-pagination';
import { Section } from '../types/palette';
import { Modes } from '../../../types/page-modes';
import { Placemark } from './types';
import { usePostFormDataMutation, usePutFormDataMutation } from '../../../hooks/use-form-mutations';

type Item = {
  id: string;
  [key: string]: any;
};

export type Props = {
  pageState: {
    mode: Modes;
    item?: Item;
  };
  setPageState: (mode: Modes, item?: Item) => void;
};

export const PlacemarkEditor = ({ pageState, setPageState }: Props): JSX.Element => {
  const { register: createRegister, handleSubmit: handleCreateSubmit, formState: { errors } } = useForm();
  const { register: updateRegister, handleSubmit: handleUpdateSubmit, setValue: setUpdateValue, watch, formState: { errors: updateFormErrors } } = useForm();
  const uploadIcon = watch('uploadIcon');
  const inputRef = useRef<HTMLInputElement>();
  const itemId = pageState.item?.id as string;
  const params = useParams();
  const sections = usePagination<Pageable<Section>>('/palette/sections', { size: 9999 });
  const postFormDataMutation = usePostFormDataMutation({ path: '/placemarks', cacheKey: `/placemarks/section/${params?.sectionId as string}` });
  const putFormDataMutation = usePutFormDataMutation({ path: `/placemarks/${itemId}`, cacheKey: `/placemarks/section/${params?.sectionId as string}` });
  const deleteMutation = useDeleteMutation({ cacheKey: `/placemarks/section/${params?.sectionId as string}` });

  useEffect(() => {
    if (pageState.mode === Modes.UPDATE) {
      onUpdate();
    }
  }, []);

  useEffect(() => {
    if (postFormDataMutation.isSuccess || putFormDataMutation.isSuccess) {
      setPageState(Modes.BROWSE);
    }
  }, [postFormDataMutation.isSuccess, putFormDataMutation.isSuccess]);

  const onDeleteConfirm = (): void => {
    setPageState(Modes.BROWSE);
    deleteMutation.mutate({ path: `/placemarks/${itemId}` });
  };

  const onSubmit = (data: FieldValues): void => {
    !data.id ? postFormDataMutation.mutate(data) : putFormDataMutation.mutate(data);
  };

  const onUpdate = (): void => {
    for (const key in pageState.item) {
      setUpdateValue(key, pageState.item[key as keyof Placemark]);
    }
  };

  const triggerUpload = (): void => {
    inputRef.current?.click?.();
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUpdateValue('uploadIcon', event.target.files);
  };

  return <>
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.CANCEL)} backdropClose={false}>
        <form onSubmit={handleCreateSubmit(onSubmit)}>
          <Title>Add Placemark</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">Place Mark Name</Label>
              <TextInputLarge {...createRegister('name', { required: 'You must provide a value for Place Mark Name.' })} placeholder="Place Mark Name" autoFocus errors={errors} />
            </div>
            <div className="mb-4">
              <Label>Section</Label>
              <Select2 { ...createRegister('sectionId', { required: 'You must provide a value for Section.' })} errors={errors}>
                <option value="">Select Section</option>
                {sections.data?.content.map(section => <option key={section.id} value={section.id}>{section.name}</option>)}
              </Select2>
            </div>
            <div className="mb-4">
              <Label>Place Mark Icon</Label>
              <FileInputLarge { ...createRegister('uploadIcon', { required: 'You must provide a value for Place mark Icon.' })} errors={errors} accept="image/png" />
              <span className="supported-file-format">Supported image format: PNG</span>
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.CANCEL)} disabled={postFormDataMutation.isLoading}>Cancel</Button.Tertiary>
            <Button.Primary type="submit" disabled={postFormDataMutation.isLoading}>
              <Icons.Add /> Add
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.UPDATE && (
      <Dialog onCancel={(): void => setPageState(Modes.CANCEL)} backdropClose={false}>
        <form onSubmit={handleUpdateSubmit(onSubmit)}>
          <Title>Edit Placemark</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">Place Mark Name</Label>
              <TextInputLarge {...updateRegister('name', { required: 'You must provide a value for Place Mark Name.' })} errors={updateFormErrors} />
            </div>
            <div className="mb-4">
              <Label>Section</Label>
              <Select2 { ...updateRegister('sectionId', { required: 'You must provide a value for Section.' })} errors={updateFormErrors}>
                <option value="">Select Section</option>
                {sections.data?.content.map(section => <option key={section.id} value={section.id}>{section.name}</option>)}
              </Select2>
            </div>
            {!uploadIcon ? <div className="mb-4">
              <Label className="offset-label-icon">Placemark url</Label>
              <div className="d-flex" onClick={triggerUpload}>
                <div className="icon-wrapper">
                  <img src={pageState.item?.icon} width={29} height={29} />
                </div>
                <div className="text-input">
                  {pageState.item?.icon}
                </div>
              </div>
            </div> : <span />}
            <div className="mb-4" style={{ display: uploadIcon ? '' : 'none' }}>
              <Label>Place Mark Icon</Label>
              <FileInputLarge { ...updateRegister('uploadIcon', { required: Boolean(uploadIcon) && 'You must provide a value for Place Mark Icon.' }) } errors={updateFormErrors} accept="image/png" ref={inputRef} onChange={onFileChange} />
              <span className="supported-file-format">Supported image format: PNG</span>
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.CANCEL)} disabled={putFormDataMutation.isLoading}>Cancel</Button.Tertiary>
            <Button.Primary type="submit" disabled={putFormDataMutation.isLoading}>
              Update
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.CANCEL)} backdropClose={false}>
        <Title>Delete Placemark</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.CANCEL)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
